import { Card } from "@components/cards/card";
import { Input } from "@components/forms/input";
import { Select } from "@components/forms/select";
import { Pagination } from "@components/pagination";
import { IMyRaffleRewardClaimListItemDTO } from "@hooks/dtos/raffles/IRaffleRewardClaimDTO";
import {
  RaffleRewardClaimDeliveryTypes,
  RaffleRewardClaimStatusTypes,
} from "@hooks/enums/raffle_reward_claim";
import { Masks } from "@hooks/functions/Masks/Masks";
import {
  raffleRewardClaimDeliveryTypeSelect,
  raffleRewardClaimStatusSelect,
} from "@hooks/functions/Raffle/rewardClaim";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import { useEffect, useState } from "react";
import { FaBroom, FaExpand, FaGift, FaMinus } from "react-icons/fa6";
import {
  RewardCampaignDesktopRow,
  RewardCampaignMobileCard,
} from "./components/RewardCampaignRows";

const masks = new Masks();

const takeOptions = [
  { value: 10, name: "10 registros" },
  { value: 25, name: "25 registros" },
  { value: 50, name: "50 registros" },
  { value: 100, name: "100 registros" },
];

const orderOptions = [
  { value: "DESC", name: "Recentes" },
  { value: "ASC", name: "Antigas" },
];

interface IRewardFilters {
  status: RaffleRewardClaimStatusTypes | "";
  deliveryType: RaffleRewardClaimDeliveryTypes | "";
}

interface IRewardListOverride {
  page?: number;
  status?: RaffleRewardClaimStatusTypes | "";
  deliveryType?: RaffleRewardClaimDeliveryTypes | "";
  startDate?: string;
  endDate?: string;
}

export function MyRewards() {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [show, setShow] = useState({ filter: true });
  const [filters, setFilters] = useState<IRewardFilters>({
    status: "",
    deliveryType: "",
  });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(10);
  const [order, setOrder] = useState<"DESC" | "ASC">("DESC");
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [rewards, setRewards] = useState<IMyRaffleRewardClaimListItemDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  function cleanFilters() {
    setFilters({ status: "", deliveryType: "" });
    setStartDate("");
    setEndDate("");
    setPage(1);
    handleRewardList({
      page: 1,
      status: "",
      deliveryType: "",
      startDate: "",
      endDate: "",
    });
  }

  async function handleRewardList(override?: IRewardListOverride) {
    setIsLoading(true);

    try {
      const params = {
        page: override?.page ?? page,
        take,
        order,
        status: override?.status ?? filters.status,
        deliveryType: override?.deliveryType ?? filters.deliveryType,
        startDate: override?.startDate ?? startDate,
        endDate: override?.endDate ?? endDate,
      };

      const result = await api.get(`/raffleClient/rewards/my`, { params });
      const nextRewards = result.data?.list || [];
      const nextTotal = Number(result.data?.total || nextRewards.length);
      const nextTotalPages =
        Number(result.data?.totalPages) || Math.ceil(nextTotal / take) || 1;

      setRewards(Array.isArray(nextRewards) ? nextRewards : []);
      setTotal(nextTotal);
      setTotalPages(nextTotalPages);
    } catch (err: any) {
      pushToastify({
        message:
          err?.response?.data?.message ?? "Falha ao carregar seus prêmios.",
        type: "error",
        theme: "dark",
      });
      setRewards([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    handleRewardList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, take, order]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <section className="bg-light pt-5 pb-4 px-3 overflow-hidden">
      <div className="container px-3 px-md-4 px-lg-0">
        <div className="mb-4">
          <h1 className="h3 fw-bold mb-1">Meus Prêmios</h1>
          <p className="text-muted mb-0">
            Acompanhe os prêmios recebidos nas campanhas em que você foi
            ganhador.
          </p>
        </div>

        <div className="row">
          <div className="col-12">
            <Card>
              <button
                type="button"
                className="card-header border-0 text-start w-100"
                onClick={() =>
                  setShow((prev) => ({ ...prev, filter: !prev.filter }))
                }
              >
                <div className="d-flex justify-content-between gap-3">
                  <div>
                    <div className="card-title">Filtrar Prêmios</div>
                    <div className="card-category">
                      Utilize o filtro para consultar suas premiações.
                    </div>
                  </div>
                  <div>
                    {show.filter ? (
                      <FaMinus
                        className="fs-5"
                        title="Clique para ocultar os filtros."
                      />
                    ) : (
                      <FaExpand
                        className="fs-5"
                        title="Clique para visualizar os filtros."
                      />
                    )}
                  </div>
                </div>
              </button>

              <div className={`card-body ${show.filter ? "" : "d-none"}`}>
                <div className="row g-3 align-items-end">
                  <div className="col-12 col-lg-3">
                    <Select
                      animated
                      label="Status do Prêmio"
                      className="form-select form-control"
                      aria-label="Status do prêmio"
                      value={filters.status}
                      options={raffleRewardClaimStatusSelect}
                      onChange={(e) => {
                        setFilters((prev) => ({
                          ...prev,
                          status: e.target
                            .value as RaffleRewardClaimStatusTypes | "",
                        }));
                      }}
                    />
                  </div>
                  <div className="col-12 col-lg-3">
                    <Select
                      animated
                      label="Tipo de Entrega"
                      className="form-select form-control"
                      aria-label="Tipo de entrega"
                      value={filters.deliveryType}
                      options={raffleRewardClaimDeliveryTypeSelect}
                      onChange={(e) => {
                        setFilters((prev) => ({
                          ...prev,
                          deliveryType: e.target
                            .value as RaffleRewardClaimDeliveryTypes | "",
                        }));
                      }}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <Input
                      animated
                      id="startDate"
                      label="Data Inicial"
                      placeholder=""
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <Input
                      animated
                      id="endDate"
                      label="Data Final"
                      placeholder=""
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <div className="d-flex flex-column flex-sm-row justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary px-4 order-2 order-sm-1"
                        onClick={cleanFilters}
                        title="Limpar todos os filtros."
                      >
                        <FaBroom className="me-2" />
                        Limpar
                      </button>
                      <button
                        type="button"
                        className="btn btn-success px-5 order-1 order-sm-2"
                        onClick={() => {
                          setPage(1);
                          handleRewardList({ page: 1 });
                        }}
                      >
                        Filtrar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        <Card className="mt-4">
          <div className="card-header">
            <div className="row align-items-center g-3">
              <div className="col-12 col-lg-8">
                <div className="card-title">Prêmios Recebidos</div>
                <div className="card-category">
                  Lista agrupada por campanha premiada.
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="d-flex flex-column flex-sm-row justify-content-lg-end gap-2">
                  <div style={{ minWidth: 150 }}>
                    <Select
                      animated
                      label="Registros"
                      className="form-select form-control"
                      aria-label="Quantidade de registros por página"
                      value={String(take)}
                      options={takeOptions}
                      onChange={(e) => {
                        setTake(Number(e.target.value));
                        setPage(1);
                      }}
                    />
                  </div>
                  <div style={{ minWidth: 150 }}>
                    <Select
                      animated
                      label="Ordenação"
                      className="form-select form-control"
                      aria-label="Ordenação da lista"
                      value={order}
                      options={orderOptions}
                      onChange={(e) => {
                        setOrder(e.target.value as "DESC" | "ASC");
                        setPage(1);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card-body">
            <div className="table-responsive d-none d-lg-block">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th scope="col" className="text-start text-nowrap">
                      Campanha
                    </th>
                    <th scope="col" className="text-center text-nowrap">
                      Ticket Vencedor
                    </th>
                    <th scope="col" className="text-start text-nowrap">
                      Prêmios
                    </th>
                    <th scope="col" className="text-end text-nowrap">
                      Atualização
                    </th>
                    <th scope="col" className="text-end text-nowrap">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading || rewards.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-5">
                        <div className="fw-semibold">
                          {isLoading
                            ? "Carregando prêmios..."
                            : "Nenhum prêmio encontrado."}
                        </div>
                        {!isLoading && (
                          <small className="text-muted">
                            Ajuste os filtros para consultar outras premiações.
                          </small>
                        )}
                      </td>
                    </tr>
                  ) : (
                    rewards.map((item) => (
                      <RewardCampaignDesktopRow
                        item={item}
                        key={item.raffle.key}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-lg-none">
              <div className="mb-2">
                <small className="fw-semibold text-muted">
                  Visualização em dispositivos móveis
                </small>
              </div>

              <div className="row g-3">
                {isLoading || rewards.length === 0 ? (
                  <div className="col-12 text-center py-4">
                    <FaGift className="text-muted mb-2" />
                    <div className="fw-semibold">
                      {isLoading
                        ? "Carregando prêmios..."
                        : "Nenhum prêmio encontrado."}
                    </div>
                    {!isLoading && (
                      <small className="text-muted">
                        Ajuste os filtros para consultar outras premiações.
                      </small>
                    )}
                  </div>
                ) : (
                  rewards.map((item) => (
                    <div className="col-12" key={`mobile-${item.raffle.key}`}>
                      <RewardCampaignMobileCard item={item} />
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="d-flex flex-column flex-md-row gap-3 justify-content-between align-items-md-center border-top mt-3 pt-3">
              <div>
                <small className="text-muted">
                  Página {page} de {totalPages} página
                  {totalPages > 1 ? `s` : ``} ({masks.formatMilhar(total)}{" "}
                  registro
                  {total > 1 ? `s` : ``} encontrado{total > 1 ? `s` : ``})
                </small>
              </div>

              <div>
                <Pagination
                  page={page}
                  setPage={setPage}
                  totalPages={totalPages}
                  maxPageShow={5}
                />
              </div>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
}
